import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './SlotsManager.scss';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SERVICES    = ['Whitening', 'Implants', 'Root Canal', 'Dentures'];
const DEPARTMENTS = ['General', 'Cosmetic', 'Orthodontics', 'Surgery'];

const EMPTY_FORM = {
  date: '', start_time: '', end_time: '',
  doctor_name: '', service: 'Whitening', department: 'General',
  max_patients: 1, is_active: true,
};

const DAYS_OF_WEEK = [
  { label: 'Mon', value: 0 },
  { label: 'Tue', value: 1 },
  { label: 'Wed', value: 2 },
  { label: 'Thu', value: 3 },
  { label: 'Fri', value: 4 },
  { label: 'Sat', value: 5 },
  { label: 'Sun', value: 6 },
];

const EMPTY_BULK = {
  start_date: '', end_date: '',
  days_of_week: [0, 1, 2, 3, 4],
  start_time: '', end_time: '',
  doctor_name: '', service: 'Whitening', department: 'General',
  max_patients: 1, is_active: true,
};

export default function SlotsManager() {
  const { authFetch } = useAuth();
  const [slots, setSlots]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId]       = useState(null); // null = create, string = edit
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving]       = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk create modal
  const [showBulk, setShowBulk]       = useState(false);
  const [bulkForm, setBulkForm]       = useState(EMPTY_BULK);
  const [bulkError, setBulkError]     = useState('');
  const [bulkSaving, setBulkSaving]   = useState(false);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/slots/all`);
      if (!res.ok) throw new Error('Failed to load slots');
      setSlots(await res.json());
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Unable to connect to the server.'
        : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // Lock body scroll when any modal is open
  useEffect(() => {
    document.body.style.overflow = showModal || deleteId || showBulk ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal, deleteId, showBulk]);

  function openBulk() {
    setBulkForm(EMPTY_BULK);
    setBulkError('');
    setShowBulk(true);
  }

  function closeBulk() {
    setShowBulk(false);
    setBulkForm(EMPTY_BULK);
    setBulkError('');
  }

  function handleBulkChange(e) {
    const { name, value, type, checked } = e.target;
    setBulkForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  function toggleBulkDay(day) {
    setBulkForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day],
    }));
  }

  async function handleBulkSave(e) {
    e.preventDefault();
    setBulkError('');
    if (bulkForm.days_of_week.length === 0) {
      setBulkError('Select at least one day of the week.');
      return;
    }
    setBulkSaving(true);
    try {
      const payload = {
        ...bulkForm,
        max_patients: Number(bulkForm.max_patients),
        days_of_week: bulkForm.days_of_week,
      };
      const res = await authFetch(`${API}/api/slots/bulk`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(
          Array.isArray(detail) ? detail.map(e => e.msg || String(e)).join('; ')
            : (typeof detail === 'string' ? detail : 'Failed to create slots')
        );
      }
      setSuccess(`${data.created} slot${data.created !== 1 ? 's' : ''} created successfully.`);
      closeBulk();
      loadSlots();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setBulkError(err.message === 'Failed to fetch' ? 'Unable to connect to the server.' : err.message);
    } finally {
      setBulkSaving(false);
    }
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setFormError('');
    setShowModal(true);
  }

  function openEdit(slot) {
    setForm({
      date: slot.date,
      start_time: slot.start_time,
      end_time: slot.end_time,
      doctor_name: slot.doctor_name,
      service: slot.service,
      department: slot.department,
      max_patients: slot.max_patients,
      is_active: slot.is_active,
    });
    setEditId(slot._id);
    setFormError('');
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditId(null);
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = { ...form, max_patients: Number(form.max_patients) };
      const url  = editId ? `${API}/api/slots/${editId}` : `${API}/api/slots/`;
      const method = editId ? 'PUT' : 'POST';
      const res = await authFetch(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map(e => e.msg || String(e)).join('; ')
          : (typeof detail === 'string' ? detail : 'Failed to save slot');
        throw new Error(msg);
      }
      setSuccess(editId ? 'Slot updated.' : 'Slot created.');
      closeModal();
      loadSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setFormError(err.message === 'Failed to fetch'
        ? 'Unable to connect to the server.'
        : err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/api/slots/${deleteId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error('Failed to delete slot');
      setSuccess('Slot deleted.');
      setDeleteId(null);
      loadSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="slm-page">
      <div className="slm-header">
        <h2 className="slm-title">Slots Manager</h2>
        <div className="slm-header-actions">
          <button className="btn slm-bulk-btn" onClick={openBulk}>
            ↻ Repeat Slot
          </button>
          <button className="btn slm-add-btn" onClick={openCreate}>
            + Add Slot
          </button>
        </div>
      </div>

      {error   && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="slm-loading">
          <div className="spinner-border" style={{ color: '#1a237e' }} role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="slm-table-wrap">
          <table className="table table-hover slm-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Doctor</th>
                <th>Service</th>
                <th>Department</th>
                <th>Max</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slots.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No slots found. Create one above.
                  </td>
                </tr>
              ) : (
                slots.map(slot => (
                  <tr key={slot._id}>
                    <td>{slot.date}</td>
                    <td>{slot.start_time}–{slot.end_time}</td>
                    <td className="slm-doctor">{slot.doctor_name}</td>
                    <td>{slot.service}</td>
                    <td>{slot.department}</td>
                    <td>{slot.max_patients}</td>
                    <td>
                      <span className={`badge ${slot.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {slot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="slm-actions">
                      <button
                        className="btn btn-sm slm-edit-btn"
                        onClick={() => openEdit(slot)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm slm-delete-btn"
                        onClick={() => setDeleteId(slot._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="slm-modal-backdrop">
          <div className="slm-modal">
            <div className="slm-modal-header">
              <h3>{editId ? 'Edit Slot' : 'New Slot'}</h3>
              <button className="slm-modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSave} className="slm-modal-body">
              {formError && (
                <div className="alert alert-danger slm-form-error">{formError}</div>
              )}

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    name="date"
                    className="form-control"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    className="form-control"
                    value={form.start_time}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    className="form-control"
                    value={form.end_time}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Doctor Name</label>
                  <input
                    type="text"
                    name="doctor_name"
                    className="form-control"
                    value={form.doctor_name}
                    onChange={handleChange}
                    placeholder="Dr. Jane Smith"
                    required
                    minLength={2}
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">Service</label>
                  <select
                    name="service"
                    className="form-select"
                    value={form.service}
                    onChange={handleChange}
                  >
                    {SERVICES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="col-6">
                  <label className="form-label">Department</label>
                  <select
                    name="department"
                    className="form-select"
                    value={form.department}
                    onChange={handleChange}
                  >
                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>

                <div className="col-6">
                  <label className="form-label">Max Patients</label>
                  <input
                    type="number"
                    name="max_patients"
                    className="form-control"
                    value={form.max_patients}
                    onChange={handleChange}
                    min={1}
                    max={10}
                    required
                  />
                </div>

                <div className="col-6 d-flex align-items-end pb-1">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="slm-is-active"
                      name="is_active"
                      className="form-check-input"
                      checked={form.is_active}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="slm-is-active">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="slm-modal-footer">
                <button
                  type="button"
                  className="btn slm-cancel-btn"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn slm-save-btn" disabled={saving}>
                  {saving ? 'Saving…' : (editId ? 'Update Slot' : 'Create Slot')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulk && (
        <div className="slm-modal-backdrop">
          <div className="slm-modal slm-bulk-modal">
            <div className="slm-modal-header">
              <h3>Repeat Slot</h3>
              <button className="slm-modal-close" onClick={closeBulk}>×</button>
            </div>

            <form onSubmit={handleBulkSave} className="slm-modal-body">
              {bulkError && (
                <div className="alert alert-danger slm-form-error">{bulkError}</div>
              )}

              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    className="form-control"
                    value={bulkForm.start_date}
                    onChange={handleBulkChange}
                    required
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    className="form-control"
                    value={bulkForm.end_date}
                    min={bulkForm.start_date}
                    onChange={handleBulkChange}
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Days of Week</label>
                  <div className="slm-days-row">
                    {DAYS_OF_WEEK.map(({ label, value }) => (
                      <button
                        key={value}
                        type="button"
                        className={`slm-day-btn ${bulkForm.days_of_week.includes(value) ? 'slm-day-btn--active' : ''}`}
                        onClick={() => toggleBulkDay(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-6">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    className="form-control"
                    value={bulkForm.start_time}
                    onChange={handleBulkChange}
                    required
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    className="form-control"
                    value={bulkForm.end_time}
                    onChange={handleBulkChange}
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Doctor Name</label>
                  <input
                    type="text"
                    name="doctor_name"
                    className="form-control"
                    value={bulkForm.doctor_name}
                    onChange={handleBulkChange}
                    placeholder="Dr. Jane Smith"
                    required
                    minLength={2}
                  />
                </div>

                <div className="col-6">
                  <label className="form-label">Service</label>
                  <select name="service" className="form-select" value={bulkForm.service} onChange={handleBulkChange}>
                    {SERVICES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="col-6">
                  <label className="form-label">Department</label>
                  <select name="department" className="form-select" value={bulkForm.department} onChange={handleBulkChange}>
                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>

                <div className="col-6">
                  <label className="form-label">Max Patients</label>
                  <input
                    type="number"
                    name="max_patients"
                    className="form-control"
                    value={bulkForm.max_patients}
                    onChange={handleBulkChange}
                    min={1}
                    max={10}
                    required
                  />
                </div>

                <div className="col-6 d-flex align-items-end pb-1">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="slm-bulk-active"
                      name="is_active"
                      className="form-check-input"
                      checked={bulkForm.is_active}
                      onChange={handleBulkChange}
                    />
                    <label className="form-check-label" htmlFor="slm-bulk-active">Active</label>
                  </div>
                </div>
              </div>

              <div className="slm-modal-footer">
                <button type="button" className="btn slm-cancel-btn" onClick={closeBulk}>
                  Cancel
                </button>
                <button type="submit" className="btn slm-save-btn" disabled={bulkSaving}>
                  {bulkSaving ? 'Creating…' : 'Create Slots'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="slm-modal-backdrop">
          <div className="slm-modal slm-confirm-modal">
            <div className="slm-modal-header">
              <h3>Delete Slot?</h3>
            </div>
            <div className="slm-modal-body">
              <p>This action cannot be undone. Any existing appointments on this slot will remain in the system.</p>
            </div>
            <div className="slm-modal-footer">
              <button className="btn slm-cancel-btn" onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button
                className="btn slm-delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
